import { useState, useEffect } from 'react';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import toast from 'react-hot-toast';

import Modal from './Modal';
import ButtonLoader from './ButtonLoader';

import { getSydneyDateObject, buildSydneyDateTime } from '../utils/dateUtils';

const hourOptions = Array.from({ length: 24 }, (_, i) => ({ value: i, label: i }));
const minuteOptions = Array.from({ length: 60 }, (_, i) => ({ value: i, label: i }));

const CloseSessionModal = ({ isOpen, session, isClosing, onConfirm, onCancel }) => {
  const [endDate, setEndDate] = useState(null);
  const [endHour, setEndHour] = useState(null);
  const [endMinute, setEndMinute] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const now = getSydneyDateObject(new Date());
      setEndDate(now);
      setEndHour({ value: now.getHours(), label: now.getHours() });
      setEndMinute({ value: now.getMinutes(), label: now.getMinutes() });
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const endTime = buildSydneyDateTime(endDate, endHour.value, endMinute.value);

    if (endTime < new Date(session.startTime)) {
      toast.error('End time cannot be before the session start time.');
      return;
    }

    if (endTime > new Date()) {
      toast.error('End time cannot be in the future.');
      return;
    }

    onConfirm(endTime);
  };

  const minDate = session?.startTime ? getSydneyDateObject(session.startTime) : undefined;
  const maxDate = getSydneyDateObject(new Date());

  return (
    <Modal isOpen={isOpen} overflowType="visible">
      <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[480px]">
        <div className="flex items-center justify-between bg-ase-blue text-white p-4 font-bold rounded-t-md">
          <p>Close Session</p>
          <p onClick={onCancel} className="font-bold cursor-pointer text-2xl">X</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col p-4 gap-4">
          <p className="text-sm text-gray-600">
            Closing <span className="font-semibold">{session?.labourer?.user?.fullName}</span>'s session at <span className="font-semibold">{session?.project?.name}</span>. Set the end time below.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1">
              <span>End date</span>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                required
                clearIcon={null}
                format="d/M/yyyy"
                className="h-[46px]"
                maxDetail="month"
                minDetail="year"
                minDate={minDate}
                maxDate={maxDate}
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-1">
                <span>Hour</span>
                <Select
                  value={endHour}
                  onChange={setEndHour}
                  options={hourOptions}
                  required
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span>Minute</span>
                <Select
                  value={endMinute}
                  onChange={setEndMinute}
                  options={minuteOptions}
                  required
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </label>
            </div>
          </div>

          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 h-[48px] border border-gray-300 rounded-md text-sm font-semibold text-gray-700 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isClosing}
              className="flex-1 h-[48px] bg-red-600 text-white rounded-md text-sm font-semibold flex items-center justify-center cursor-pointer disabled:opacity-50"
            >
              {isClosing ? <ButtonLoader /> : 'Close Session'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CloseSessionModal;
